// ViewTrainings.js

import React, { useState, useEffect } from 'react';
import { Button, Form, Tab, Tabs, Alert, Badge } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { getUser } from '../utils/auth';
import Swal from 'sweetalert2';
import AttachmentsTab from './AttachmentsTab';
import ParticipantsTab from './TrainingParticipants';
import AttendanceTab from './TrainingAttendance';
import AttendeeListTab from './TrainingAttendeeList';

// Non-blocking indicator only — held/expired/missing never affects whether
// Apply is enabled, matches get_user_qualification_status()'s three states.
function qualificationStatusBadge(status) {
  if (status === 'held') return <Badge bg="success">Held</Badge>;
  if (status === 'expired') return <Badge bg="warning" text="dark">Expired</Badge>;
  return <Badge bg="danger">Missing</Badge>;
}

function ParticipantTrainingView() {
  const { id } = useParams();
  // Base API URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const loggedInUser = getUser();
  console.log ("loggedinuser ", loggedInUser);

  // Uploading training documents is restricted to Trainer, Training Admin
  // (vendor), and Super Admin (admin/masteradmin) — everyone else can still
  // view/download existing attachments, just not add new ones.
  const canUploadAttachments = ['admin', 'masteradmin', 'trainingadmin', 'trainer'].includes(loggedInUser.role);

  const [training, setTraining] = useState(null);
  const [isKeyInstructor, setIsKeyInstructor] = useState(false);
  const [feedback, setFeedback] = useState({ success: null, message: '' });

  const [seatsTaken, setSeatsTaken] = useState(0);
  const [alreadyApplied, setAlreadyApplied] = useState(false);

  const [key, setKey] = useState('details');
  
  // Fetch training and registration data
  useEffect(() => {
    if (!id) return;

    // Training details
    fetch(`${API_BASE_URL}/trainings/get_training.php?id=${id}`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setTraining({
          id: data.id,
          referenceCode: data.code,
          title: data.title,
          description: data.description,
          dateRange: data.dateRange,
          startTime: data.startTime,
          endTime: data.endTime,
          location: data.location,
          max_seat: data.max_seat,
          assignedTrainer: data.assignedTrainer,
          cancelReason: data.cancelReason,
          status: data.status,
          prerequisites: data.prerequisites || [],
          qualificationPrerequisites: data.qualificationPrerequisites || []
        });
      })
      .catch(error => {
          console.error('Error fetching training:', error);
          setFeedback({ success: false, message: 'Failed to load training details.' });
        });

    // Registration info
    
    /*
    fetch(`${API_BASE_URL}/trainings/get_registration_info.php?id=${id}`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
         console.log("data",data);
        setSeatsTaken(data.seatsTaken);
        setAlreadyApplied(data.alreadyApplied);
      })
      .catch(() => {
        console.error('Error fetching registration info');
      });
      */
      fetch(`${API_BASE_URL}/trainings/get_registration_info.php?id=${id}`, {
        credentials: 'include'
      })
        .then(async (res) => {
          if (!res.ok) {
            const text = await res.text();
            throw new Error(`HTTP ${res.status}: ${text}`);
          }
          return res.json();
        })
        .then(data => {
          console.log("Registration data:", data);
          setSeatsTaken(data.seatsTaken);
          setAlreadyApplied(data.alreadyApplied);
        })
        .catch((error) => {
          console.error('Error fetching registration info:', {
            error: error.message,
            stack: error.stack,
            url: `${API_BASE_URL}/trainings/get_registration_info.php?id=${id}`
          });
        });
        
  }, [id]);

    const isTrainingInPastOrToday = () => {
        //return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Strip time
        const startDate = new Date(training.dateRange[0]);
        startDate.setHours(0, 0, 0, 0); // Strip time
        return startDate <= today;
    };

    const handleApply = () => {
    Swal.fire({
        title: 'Apply for Training',
        text: 'Are you sure you want to apply for this training?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Apply',
        cancelButtonText: 'Cancel',
    }).then((result) => {
        if (result.isConfirmed) {
         fetch(`${API_BASE_URL}/trainings/request_training.php?id=${id}&keyInstructor=${isKeyInstructor ? 1 : 0}`, {
            method: 'GET',
            credentials: 'include',
        })
            .then(res => res.json())
            .then(data => {
            if (data.success) {
                setFeedback({ success: true, message: 'Successfully apply for training!' });
                setAlreadyApplied(true);
                setSeatsTaken(prev => prev + 1);

                Swal.fire('Applied!', 'You have successfully applied.', 'success');
            } else {
                setFeedback({ success: false, message: data.message || 'Failed to apply.' });
                Swal.fire('Error', data.message || 'Failed to apply.', 'error');
            }
            })
            .catch(() => {
            Swal.fire('Error', 'Something went wrong during application.', 'error');
            });
        }
    });
    };

    const handleCancel = () => {
    Swal.fire({
        title: 'Cancel Application',
        text: 'Do you really want to cancel your application?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Cancel it',
        cancelButtonText: 'Keep it',
    }).then((result) => {
        if (result.isConfirmed) {
        fetch(`${API_BASE_URL}/trainings/cancel_registration.php?id=${id}`, {
            method: 'GET',
            credentials: 'include',
        })
            .then(res => res.json())
            .then(data => {
            if (data.success) {
                setFeedback({ success: true, message: 'Application cancelled successfully.' });
                setAlreadyApplied(false);
                setSeatsTaken(prev => (prev > 0 ? prev - 1 : 0));

                Swal.fire('Cancelled!', 'Your application has been cancelled.', 'success');
            } else {
                Swal.fire('Error', data.message || 'Cancellation failed.', 'error');
            }
            })
            .catch(() => {
            Swal.fire('Error', 'Something went wrong during cancellation.', 'error');
            });
        }
    });
    };

  if (!training) {
    return <p>Loading training details...</p>;
  }

  const seatsAvailable = training.max_seat - seatsTaken;
  const disableApply = alreadyApplied || seatsAvailable <= 0;

  return (
    <div>
      <h2>Training Information</h2>

    <Tabs activeKey={key} onSelect={(k) => setKey(k)} className="mb-3 no-print">
      <Tab eventKey="details" title="Training Details">

        
      <Form>
        <Form.Group>
          <Form.Label>Reference Code</Form.Label>
          <Form.Control type="text" value={training.referenceCode} readOnly />
        </Form.Group>
        <Form.Group>
          <Form.Label>Title</Form.Label> {training.title}
          
        </Form.Group>
        <Form.Group>
          <Form.Label>Description</Form.Label>
          <Form.Control as="textarea" value={training.description} rows={3} readOnly />
        </Form.Group>
        <Form.Group>
          <Form.Label>Prerequisite Course(s)</Form.Label>
          <Form.Control
            type="text"
            value={training.prerequisites.length > 0
              ? training.prerequisites.map(p => `${p.code} — ${p.title}`).join(', ')
              : 'None'}
            readOnly
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Prerequisite Qualification(s)</Form.Label>
          {training.qualificationPrerequisites.length > 0 ? (
            <div>
              {training.qualificationPrerequisites.map(q => (
                <div key={q.id}>
                  {q.name} ({q.type}) {qualificationStatusBadge(q.status)}
                </div>
              ))}
            </div>
          ) : (
            <Form.Control type="text" value="None" readOnly />
          )}
        </Form.Group>
        <Form.Group>
          <Form.Label>Date</Form.Label>
          <Form.Control type="text" value={`${training.dateRange[0]} to ${training.dateRange[1]}`} readOnly />
        </Form.Group>
        <Form.Group controlId="timeRange">
          <Form.Label>Time </Form.Label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Form.Control
              type="time"
              name="startTime"
              value={training.startTime}
              style={{ width: '130px' }}
               readOnly
            />
            <span>to</span>
            <Form.Control
              type="time"
              name="endTime"
              value={training.endTime}
              style={{ width: '130px' }}
               readOnly
            />
          </div>
        </Form.Group>
        <Form.Group>
          <Form.Label>Location</Form.Label>
          <Form.Control type="text" value={training.location} readOnly />
        </Form.Group>
        <Form.Group>
          <Form.Label>Trainer</Form.Label>
          <Form.Control type="text" value={training.assignedTrainer || '-'} readOnly />
        </Form.Group>
        {(training.status === 'Canceled' || training.status === 'Postponed') && (
          <Form.Group>
            <Form.Label>{training.status === 'Postponed' ? 'Postpone Reason' : 'Cancel Reason'}</Form.Label>
            <Form.Control as="textarea" value={training.cancelReason} rows={3} readOnly />
          </Form.Group>
        )}
        <Form.Group>
          <Form.Label>Seats Taken / Available</Form.Label>
          <Form.Control
            type="text"
            value={`${seatsTaken} taken / ${seatsAvailable > 0 ? seatsAvailable : 0} available`}
            readOnly
          />
        </Form.Group>
  

      {isTrainingInPastOrToday() ? (
        <Alert variant="warning" className="mt-3">
          This training has already started and applications are closed.
        </Alert>
      ) : (
        <>
        <Form.Group controlId="keyInstructorCheckbox">
          <Form.Check
            type="checkbox"
            label="Attend as a Key Instructor"
            checked={isKeyInstructor}
            onChange={(e) => setIsKeyInstructor(e.target.checked)}
          />
        </Form.Group>
        
      {feedback.message && (
        <Alert variant={feedback.success ? 'success' : 'danger'} className="mt-3">
          {feedback.message}
        </Alert>
      )}

        {/* Apply/Cancel Application is a self-registration action for
            employees (and the admin roles that also act as employees per
            FEATURES.md) — not trainer/trainingadmin, who deliver trainings
            rather than attend them. */}
        {!['trainingadmin', 'trainer'].includes(loggedInUser.role) && (
        <>
        {alreadyApplied ? (
            <Button
                variant="danger"
                onClick={handleCancel}
                className="me-2"
            >
                Cancel Application
            </Button>
            ) : (
            <Button
                variant="primary"
                onClick={handleApply}
                disabled={disableApply}
                className="me-2"
            >
                {seatsAvailable <= 0 ? 'Full' : 'Apply'}
            </Button>
        )}
        
        {['admin', 'masteradmin', 'companyadmin', 'departmentadmin'].includes(loggedInUser.role) && (
        <Link to={`/training-provider/assign-training/${id}`}>
        <Button className="m-1"variant="primary">Assign</Button>
        </Link>
        )}
        </>
    )}
    </>
    )}
    
    </Form>
    </Tab>
    <Tab eventKey="documents" title="Documents/Attachment">
      {training.id !== null && (
      <AttachmentsTab
        categoryId={training.id}
        category="training_documents"
        currentUserId={loggedInUser.id}
        canUpload={canUploadAttachments}
      />
    )}
    </Tab>
    {/* Participants/Attendance/Attendee List all read data an employee has
        no access to (get_training_participants.php, get_training_attendance.php
        now reject that role) — hidden rather than shown and left to fail. */}
    {loggedInUser.role !== 'employee' && (
    <Tab eventKey="participants" title="Participants">
      {training.id !== null && (
      <ParticipantsTab
        categoryId={training.id}
        currentUserId={loggedInUser.id}
      />
    )}
    </Tab>
    )}
    {/* Editable attendance/results entry is restricted to Trainer,
        Training Admin (vendor), and Super Admin (admin/masteradmin) —
        matches attended_training.php's write authorization. Other admins
        (companyadmin/departmentadmin) view/print/export the same data via
        the read-only Attendee List tab instead. */}
    {['admin', 'masteradmin', 'trainingadmin', 'trainer'].includes(loggedInUser.role) && (
    <Tab eventKey="attendance" title="Attendance">
      {training.id !== null && (
      <AttendanceTab
        categoryId={training.id}
        currentUserId={loggedInUser.id}
      />
    )}
    </Tab>
    )}
    {loggedInUser.role !== 'employee' && (
    <Tab eventKey="attendeeList" title="Attendee List">
      {training.id !== null && (
      <AttendeeListTab />
    )}
    </Tab>
    )}
    </Tabs>
    </div>
  );
}

export default ParticipantTrainingView;
